// Button — the primary interactive element for any tappable action.
// Pill-shaped (Citycare brand signature: border-radius: 800px) with
// five visual variants, one size. Default `type="button"` because
// HTML's submit-by-default is a hostile surprise inside form-heavy
// admin screens.
//
// Touch target: min 44x44 per AGENTS.md invariant #10 — both
// dimensions enforced so one-character labels (`X`, `+`) still
// clear the contract.
//
// Variants map to the Citycare brand button system:
//   primary  — Forest bg + Cream text  (main CTA on light surfaces)
//   secondary — White / outline pill  (secondary action on light surfaces)
//   lime     — Lime bg + Forest text   (high-emphasis CTA, signature pair)
//   danger   — Project Orange + white text (destructive)
//   ghost    — Transparent + Ink-soft  (tertiary)

import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { Spinner } from './spinner'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'lime'
  | 'danger'
  | 'ghost'

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant
  loading?: boolean
  children: ReactNode
}

// Variant palette is sourced from Citycare brand v1.0. Tokens flow
// from `@theme` in src/index.css — the semantic shorthands
// (`border-border`, `text-ink`, `hover:bg-surface`, etc.) hide the
// raw cc-* values behind intent-named utilities so a future palette
// tweak is a one-token change rather than a search-and-replace.
const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-[color:var(--color-cc-forest)] text-[color:var(--color-cc-cream)] hover:bg-[color:var(--color-cc-teal)] disabled:opacity-50',
  secondary:
    'border border-border bg-white text-ink hover:bg-surface disabled:opacity-50',
  lime:
    'bg-[color:var(--color-cc-lime)] text-[color:var(--color-cc-forest)] hover:brightness-95 disabled:opacity-50',
  danger:
    'bg-accent text-white hover:opacity-90 disabled:opacity-50',
  ghost:
    'text-ink-soft hover:bg-surface hover:text-ink disabled:opacity-50',
}

const BASE_CLASSES =
  'inline-flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-pill px-6 py-2 text-sm font-medium tracking-wide transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand)] disabled:cursor-not-allowed'

export function Button({
  variant = 'primary',
  loading = false,
  disabled,
  type = 'button',
  className,
  children,
  ...rest
}: ButtonProps) {
  const isDisabled = disabled === true || loading
  const classes = [BASE_CLASSES, VARIANT_CLASSES[variant], className ?? '']
    .filter((c) => c !== '')
    .join(' ')
  return (
    <button
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={classes}
      {...rest}
    >
      {loading ? <Spinner label="Loading" size="sm" /> : null}
      {children}
    </button>
  )
}
